import { Right, Role, ViewRightType } from "@dto/Users";

/**
 * Result of indexing the sharing rights of a view over a list of roles.
 * `rightPerRole` maps every role key to its view right (or null when the role
 * has no right on the view), `usedRoleKeys` lists the keys of the roles that
 * currently hold a right on the view, without duplicates.
 */
export interface ViewRoleSharing {
    rightPerRole: Map<string, Right | null>;
    usedRoleKeys: string[];
}

/**
 * Builds the sharing index of a view from freshly fetched roles.
 *
 * The result is always computed from scratch: it must not depend on (nor
 * mutate) any previously known list of used role keys, otherwise every fetch
 * would accumulate duplicated keys.
 */
export function indexViewRoleSharing(roles: Role[], viewKey: string): ViewRoleSharing {
    const rightPerRole = new Map<string, Right | null>();
    const usedRoleKeys: string[] = [];
    roles.forEach((role) => {
        const right = role.rights.find((r) => r.type === ViewRightType && r.recordKey === viewKey) ?? null;
        rightPerRole.set(role._key, right);
        if (right) {
            usedRoleKeys.push(role._key);
        }
    });
    return { rightPerRole, usedRoleKeys };
}

/**
 * Removes the right of a role on the given view, keeping every other right
 * (rights on other views and rights of other types) untouched.
 */
export function withoutViewRight(rights: Right[], viewKey: string): Right[] {
    return rights.filter((r) => !(r.type === ViewRightType && r.recordKey === viewKey));
}
